import React, { useEffect, useMemo, useState } from "react";
import { X, Edit3, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { useCountries } from "@/hooks/useCountries";
import { profileService } from "@/services/profileService";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { branchesService } from "@/services/branchesService";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import { LogoUploadField } from "@/components/molecules/LogoUploadField";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { normalizeLogoUrlForApi } from "@/lib/logoUpload";
import {
  extractDialCode,
  resolvePhoneCodeOptionValue,
  sanitizeNationalPhoneInput,
} from "@/modules/contacts/contactFormUtils";
import {
  EMPTY_ENTITY_ADDRESS,
  EntityAddressFields,
  type EntityAddressFormValues,
} from "./EntityAddressFields";
import {
  branchAddressFromApi,
  diffRecord,
  legalEntityAddressFromApi,
  persistBranchAddress,
  persistLegalEntityAddress,
  toBranchAddressPayload,
  toLegalEntityRegisteredAddressPayload,
} from "./entityAddressUtils";

interface Props {
  callerEmail: string;
  type: "LEGAL_ENTITY" | "BRANCH";
  id: string;
  initialName: string | null | undefined;
  initialEmail: string | null | undefined;
  initialPhoneNumber?: string;
  /** Signed display URL from the profile hierarchy — never raw base64 from entity GET. */
  initialLogoUrl?: string | null;
  /** Organisation context for hierarchy PATCH (required for master-user tenant scope). */
  orgLegalEntityId?: string | null;
  /** Whether this entity has a connected WhatsApp channel — gates the sync-logo action. */
  whatsappEnabled?: boolean | null;
  /** Root legal entity email is immutable. */
  emailLocked?: boolean;
  open: boolean;
  onClose: () => void;
  /** Awaited before the dialog closes, so the underlying list is refreshed by the time it reopens. */
  onUpdated: () => void | Promise<void>;
}

async function saveEntityLogo(
  type: "LEGAL_ENTITY" | "BRANCH",
  id: string,
  logoUrl: string | null,
): Promise<void> {
  const payload = { logoUrl: normalizeLogoUrlForApi(logoUrl) };
  if (type === "BRANCH") {
    await branchesService.update(id, payload);
    return;
  }
  await legalEntitiesService.update(id, payload);
}

export const EditEntityDialog: React.FC<Props> = ({
  callerEmail,
  type,
  id,
  initialName,
  initialEmail,
  emailLocked,
  initialPhoneNumber,
  initialLogoUrl,
  orgLegalEntityId,
  whatsappEnabled,
  open,
  onClose,
  onUpdated,
}) => {
  const [name, setName] = useState(initialName ?? "");
  const [email, setEmail] = useState(initialEmail ?? "");
  const [phoneDialCode, setPhoneDialCode] = useState("+44");
  const [phoneCountryCode, setPhoneCountryCode] = useState("GB");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState<EntityAddressFormValues>(EMPTY_ENTITY_ADDRESS);
  const [initialAddressSnapshot, setInitialAddressSnapshot] =
    useState<EntityAddressFormValues>(EMPTY_ENTITY_ADDRESS);
  const [persistedLogoUrl, setPersistedLogoUrl] = useState("");
  // Whether the user actually interacted with the logo control this session (upload/remove).
  // Not derived from diffing normalized URLs: a pre-existing logo's signed display URL can't be
  // reverse-mapped to its permanent fileUrl unless it was just issued by an upload in this
  // session, so normalizeLogoUrlForApi() correctly returns "" for it — comparing that against
  // itself used to look like "changed" and silently wiped untouched logos on every save.
  const [logoTouched, setLogoTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressLoadError, setAddressLoadError] = useState<string | null>(null);
  const [addressLoaded, setAddressLoaded] = useState(false);
  const [syncingLogo, setSyncingLogo] = useState(false);

  const { phoneCodeOptions, loading: loadingCountries } = useCountries();
  const dialCodes = useMemo(
    () => Array.from(new Set(phoneCodeOptions.map((o) => o.value.split("|")[0]))),
    [phoneCodeOptions],
  );
  const phoneCodeValue = useMemo(
    () => resolvePhoneCodeOptionValue(phoneDialCode, phoneCountryCode, phoneCodeOptions),
    [phoneDialCode, phoneCountryCode, phoneCodeOptions],
  );

  useEffect(() => {
    if (!open) return;

    setName(initialName ?? "");
    setEmail(initialEmail ?? "");
    const { dialCode, national } = extractDialCode(initialPhoneNumber, dialCodes);
    setPhoneDialCode(dialCode || "+44");
    setPhoneCountryCode("");
    setPhoneNumber(sanitizeNationalPhoneInput(national));
    setAddress(EMPTY_ENTITY_ADDRESS);
    setInitialAddressSnapshot(EMPTY_ENTITY_ADDRESS);
    setAddressLoadError(null);
    setAddressLoaded(false);
    setError(null);
    setPersistedLogoUrl(normalizeLogoUrlForApi(initialLogoUrl));
    setLogoTouched(false);

    let cancelled = false;
    setAddressLoading(true);

    void (async () => {
      try {
        const record =
          type === "LEGAL_ENTITY"
            ? await legalEntitiesService.getOne(id)
            : await branchesService.getOne(id);
        if (cancelled) return;

        const loaded =
          type === "LEGAL_ENTITY"
            ? legalEntityAddressFromApi(record as Parameters<typeof legalEntityAddressFromApi>[0])
            : branchAddressFromApi(record as Parameters<typeof branchAddressFromApi>[0]);
        setAddress(loaded);
        setInitialAddressSnapshot(loaded);
        setAddressLoaded(true);
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          getApiErrorMessage(err) ??
          "Could not load the saved address. Close and try again to avoid overwriting existing data.";
        setAddressLoadError(message);
      } finally {
        if (!cancelled) setAddressLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, id, type, initialName, initialEmail, initialPhoneNumber, initialLogoUrl, dialCodes]);

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  const { onOverlayClick } = useModalDismiss(open, handleClose);

  if (!open) return null;

  const handleSyncLogo = async () => {
    setSyncingLogo(true);
    try {
      const result = await profileService.syncEntityLogo(callerEmail, type, id);
      if (result.status === "SYNCED") {
        toast.success("Logo synced to WhatsApp");
      } else if (result.status === "NO_LOGO") {
        toast.info("Upload a logo above before syncing.");
      } else if (result.status === "NO_CHANNEL") {
        toast.info("Connect WhatsApp before syncing a logo.");
      } else {
        toast.error(result.warning ?? "Failed to sync logo to WhatsApp");
      }
    } catch (err: unknown) {
      const message =
        getApiErrorMessage(err) ??
        (err instanceof Error ? err.message : "Failed to sync logo to WhatsApp");
      toast.error(message);
    } finally {
      setSyncingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Name is required");
    if (addressLoading) {
      return setError("Address is still loading. Please wait before saving.");
    }
    if (addressLoadError || !addressLoaded) {
      return setError(
        addressLoadError ??
          "Address could not be loaded. Close and reopen this dialog before saving.",
      );
    }

    setSubmitting(true);
    try {
      const nationalPhone = phoneNumber.trim();
      const nextName = name.trim();
      const nextEmail = email.trim();
      const nextPhone = nationalPhone ? `${phoneDialCode}${nationalPhone}` : null;
      const initialPhone = initialPhoneNumber?.trim() || null;

      const baselineName = (initialName ?? "").trim();
      const baselineEmail = (initialEmail ?? "").trim();
      const updates: { name?: string; email?: string; phoneNumber?: string | null } = {};
      if (nextName !== baselineName) updates.name = nextName;
      if (!emailLocked && nextEmail !== baselineEmail) updates.email = nextEmail;
      if (nextPhone !== initialPhone) updates.phoneNumber = nextPhone;

      if (Object.keys(updates).length > 0) {
        await profileService.updateEntity(
          callerEmail,
          type,
          id,
          updates,
          orgLegalEntityId ?? id,
        );
      }

      if (type === "LEGAL_ENTITY") {
        const addressUpdates = diffRecord(
          toLegalEntityRegisteredAddressPayload(address),
          toLegalEntityRegisteredAddressPayload(initialAddressSnapshot),
        );
        if (Object.keys(addressUpdates).length > 0) {
          await persistLegalEntityAddress(id, addressUpdates, { includeNulls: true });
        }
      } else {
        const addressUpdates = diffRecord(
          toBranchAddressPayload(address),
          toBranchAddressPayload(initialAddressSnapshot),
        );
        if (Object.keys(addressUpdates).length > 0) {
          await persistBranchAddress(id, addressUpdates, { includeNulls: true });
        }
      }

      if (logoTouched) {
        await saveEntityLogo(type, id, persistedLogoUrl);
      }

      await onUpdated();
      handleClose();
      toast.success(`${type === "BRANCH" ? "Branch" : "Legal entity"} updated.`);
    } catch (err: unknown) {
      const message = getApiErrorMessage(err) ?? (err instanceof Error ? err.message : "Failed to update entity");
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onOverlayClick}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-bridge-border/60">
        <div className="flex items-center justify-between px-6 py-4 border-b border-bridge-border/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <Edit3 size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-bridge-fg">Edit {type === "BRANCH" ? "Branch" : "Legal Entity"}</h2>
              <p className="text-xs text-bridge-muted mt-0.5">
                Update name, contact details, address, and optional logo.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="p-1.5 rounded-lg hover:bg-slate-50 text-bridge-muted hover:text-bridge-fg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto">
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-2xl flex items-center gap-3">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              {error}
            </div>
          )}

          <LogoUploadField
            label={type === "BRANCH" ? "Branch Logo" : "Legal Entity Logo"}
            logoUrl={persistedLogoUrl}
            displayLogoUrl={initialLogoUrl}
            previewName={name}
            onChange={(value) => {
              setPersistedLogoUrl(value ?? "");
              setLogoTouched(true);
            }}
            disabled={submitting}
          />

          {whatsappEnabled && (
            <div className="flex justify-end -mt-4">
              <button
                type="button"
                onClick={handleSyncLogo}
                disabled={syncingLogo || submitting}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold text-slate-600 hover:text-[#009BF5] bg-white hover:bg-bridge-accent/5 border border-slate-200 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncingLogo ? "animate-spin" : ""}`} />
                {syncingLogo ? "Syncing..." : "Sync logo to WhatsApp"}
              </button>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
              Display Name *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. London Central Office"
              disabled={submitting}
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 rounded-2xl text-sm text-slate-800 font-medium placeholder:text-slate-400 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
              Primary Contact Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. office@example.co.uk"
              disabled={submitting || emailLocked}
              title={emailLocked ? "Email of the root legal entity cannot be changed" : undefined}
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 rounded-2xl text-sm text-slate-800 font-medium placeholder:text-slate-400 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
              Phone Number
            </label>
            <div className="flex gap-2">
              <div className="w-28 shrink-0">
                <MenuSelect
                  options={phoneCodeOptions}
                  value={phoneCodeValue}
                  maxVisibleOptions={3}
                  onChange={(v) => {
                    const [nextDialCode, nextCountry] = v.split("|");
                    setPhoneDialCode(nextDialCode);
                    if (nextCountry) setPhoneCountryCode(nextCountry);
                  }}
                  placeholder="Code"
                  loading={loadingCountries}
                  searchable
                  disabled={submitting}
                />
              </div>
              <input
                type="tel"
                inputMode="numeric"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(sanitizeNationalPhoneInput(e.target.value))}
                placeholder="7123456789"
                disabled={submitting}
                className="flex-1 min-w-0 px-5 py-4 bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 rounded-2xl text-sm text-slate-800 font-medium placeholder:text-slate-400 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {addressLoadError && (
            <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-2xl">
              {addressLoadError}
            </div>
          )}

          <EntityAddressFields
            values={address}
            onChange={(patch) => setAddress((prev) => ({ ...prev, ...patch }))}
            disabled={submitting || addressLoading || Boolean(addressLoadError)}
          />

          <div className="flex items-center justify-end gap-4 pt-4">
            <button type="button" onClick={handleClose} disabled={submitting}
              className="px-6 py-3 text-sm font-bold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 border-2 border-transparent rounded-2xl transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-50 disabled:hover:text-slate-500">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || addressLoading || Boolean(addressLoadError) || !addressLoaded}
              className="inline-flex items-center gap-2 px-8 py-3 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 rounded-2xl shadow-lg shadow-indigo-100 disabled:opacity-50 disabled:scale-100 disabled:shadow-none transition-all"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
